"use client";

import { ArrowRight, Loader, Mail, MapPin, MessageCircle, Phone, Linkedin, Twitter, Github, Facebook, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
function ReachOutForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-[var(--glass-edge)] bg-transparent p-8 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center bg-[var(--electric-aqua)] text-[var(--space-black)]">
          <MessageCircle className="size-6" />
        </div>
        <h3 className="font-default text-xl font-semibold text-[var(--steel-brushed)]">Request Sent</h3>
        <p className="mt-2 text-[var(--slate-oxide)]">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-[var(--steel-brushed)] text-[var(--steel-brushed)] hover:bg-[var(--steel-brushed)] hover:text-[var(--space-black)]">
          Send another
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="name" rules={{
      required: "Required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[var(--steel-brushed)]">Name</FormLabel>
            <FormControl>
              <Input placeholder="John Smith" className="h-12 border-[var(--glass-edge)] bg-[var(--space-black)] px-4 text-[var(--steel-brushed)] placeholder:text-[var(--slate-oxide)] focus-visible:border-[var(--electric-aqua)]" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[var(--steel-brushed)]">Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" className="h-12 border-[var(--glass-edge)] bg-[var(--space-black)] px-4 text-[var(--steel-brushed)] placeholder:text-[var(--slate-oxide)] focus-visible:border-[var(--electric-aqua)]" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[var(--steel-brushed)]">Phone (Optional)</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="555-0100" className="h-12 border-[var(--glass-edge)] bg-[var(--space-black)] px-4 text-[var(--steel-brushed)] placeholder:text-[var(--slate-oxide)] focus-visible:border-[var(--electric-aqua)]" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="address" rules={{
      required: "Required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[var(--steel-brushed)]">Address</FormLabel>
            <FormControl>
              <Input placeholder="123 Water Way, Huntsville, AL" className="h-12 border-[var(--glass-edge)] bg-[var(--space-black)] px-4 text-[var(--steel-brushed)] placeholder:text-[var(--slate-oxide)] focus-visible:border-[var(--electric-aqua)]" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="how_did_you_hear" rules={{
      required: "Required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[var(--steel-brushed)]">How did you hear about us?</FormLabel>
            <FormControl>
              <Input placeholder="Referral, Google, Social Media..." className="h-12 border-[var(--glass-edge)] bg-[var(--space-black)] px-4 text-[var(--steel-brushed)] placeholder:text-[var(--slate-oxide)] focus-visible:border-[var(--electric-aqua)]" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="border border-[var(--destructive)] bg-[var(--destructive)]/10 p-4 text-sm text-[var(--destructive)]">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="group h-12 w-full bg-[var(--electric-aqua)] px-6 font-medium text-[var(--space-black)] transition-all hover:bg-[var(--steel-brushed)] sm:w-auto">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Sending...
          </> : <>
            Submit Request
            <ArrowRight className="ml-1 size-4 transition-transform group-hover:translate-x-0.5" />
          </>}
      </Button>
    </Form>;
}
export default function Initiate() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const contactDetails = [{
    icon: Mail,
    title: "Email",
    detail: "solutions@rocketcitywaterwizards.com",
    subdetail: "24h response time"
  }, {
    icon: Phone,
    title: "Phone",
    detail: "833-H20-WZRD (833-420-9973)",
    subdetail: "Direct line"
  }, {
    icon: Clock,
    title: "Hours",
    detail: "Monday–Friday 8am–4pm, Saturday 9am–1pm, Sunday closed",
    subdetail: "Business Hours"
  }];
  const socials = [{
    icon: Twitter,
    label: "Twitter",
    href: "#"
  }, {
    icon: Linkedin,
    label: "LinkedIn",
    href: "#"
  }, {
    icon: Github,
    label: "GitHub",
    href: "#"
  }, {
    icon: Facebook,
    label: "Facebook",
    href: "#"
  }];
  return <section data-section-id="125" id="initiate-project" className="relative bg-[var(--space-black)] py-20 sm:py-28">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/4 h-72 w-72 bg-[var(--electric-aqua)]/10 blur-3xl" />
        <div className="absolute -bottom-32 right-1/4 h-72 w-72 bg-[var(--deep-current)]/10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div ref={ref} className={`overflow-hidden rounded-none border border-[var(--glass-edge)] bg-[var(--space-black)] transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="grid grid-cols-1 lg:grid-cols-5">
            <div className="relative bg-[var(--space-black)] p-8 sm:p-10 lg:col-span-3 lg:p-14">
              <div className="relative">
                <span className="inline-block font-mono text-xs uppercase tracking-[0.2em] text-[var(--electric-aqua)]">
                  [REF: 001-INIT]
                </span>
                <h2 className="font-default mt-3 text-3xl font-semibold tracking-tight text-[var(--steel-brushed)] sm:text-4xl">
                  Initiate Project
                </h2>
                <p className="mt-3 max-w-md text-sm text-[var(--slate-oxide)]">
                  Connect with our engineering team to begin your pool installation or service request.
                </p>

                <div className="mt-10 text-[var(--steel-brushed)]">
                  <FormProvider formKey="initiate_contact_form" formId="initiate_contact_form" sectionName="reach-out" defaultValues={{
                  name: "",
                  email: "",
                  phone: "",
                  address: "",
                  how_did_you_hear: ""
                }} validationRules={{
                  name: {
                    required: true
                  },
                  email: {
                    required: true,
                    pattern: "email"
                  },
                  phone: {
                    pattern: "phone"
                  },
                  address: {
                    required: true
                  },
                  how_did_you_hear: {
                    required: true
                  }
                }} submitText="Submit Request" successMessage="Request received. Our team will contact you shortly." errorMessage="Error. Please try again.">
                    <ReachOutForm />
                  </FormProvider>
                </div>
              </div>
            </div>

            <div className="relative bg-[var(--steel-brushed)] p-8 sm:p-10 lg:col-span-2 lg:p-12">
              <div className="relative">
                <span className="inline-block font-mono text-xs uppercase tracking-[0.2em] text-[var(--space-black)]">
                  Contact Data
                </span>
                <h3 className="font-default mt-3 text-2xl font-semibold tracking-tight text-[var(--space-black)]">
                  Direct Support
                </h3>

                <ul className="mt-8 space-y-4">
                  {contactDetails.map((item, i) => {
                  const Icon = item.icon;
                  return <li key={i} className="group flex items-start gap-4 border border-[var(--glass-edge)] bg-[var(--space-black)]/5 p-4">
                        <div className="flex size-11 shrink-0 items-center justify-center bg-[var(--electric-aqua)] text-[var(--space-black)]">
                          <Icon className="size-5" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-default text-sm font-semibold text-[var(--space-black)]">{item.title}</p>
                          </div>
                          <p className="mt-0.5 truncate text-sm text-[var(--space-black)]/80">{item.detail}</p>
                          <p className="text-xs text-[var(--slate-oxide)]">{item.subdetail}</p>
                        </div>
                      </li>;
                })}
                </ul>

                <div className="mt-10">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--space-black)]">Follow</p>
                  <div className="mt-4 flex items-center gap-3">
                    {socials.map((s, i) => {
                    const Icon = s.icon;
                    return <Link key={i} to={s.href} className="flex size-10 items-center justify-center bg-[var(--electric-aqua)] text-[var(--space-black)] transition-transform hover:scale-105" aria-label={s.label}>
                          <Icon className="size-4" />
                        </Link>;
                  })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}